import { useEffect, useState, type CSSProperties } from 'react'
import { useCountUp, useReducedMotion } from './motion'

// A ring with the count inside: 4 of 7 evidence items, 2 of 4 sub-processors.
//
// # HOW IT SWEEPS
//
// The ring is a conic gradient driven by one custom property, --kit-p, which
// kit.css registers with @property so the browser can animate a number rather
// than snap a string. The share is set one frame after the ring is drawn, so
// it sweeps up from nothing on arrival and from its old value on a change; the
// count inside rolls with it. With reduced motion both are simply the value.

export function ProgressRing({ done, total, label }: { done: number; total: number; label: string }) {
  const reduced = useReducedMotion()
  const share = total > 0 ? Math.max(0, Math.min(100, (done / total) * 100)) : 0
  const [p, setP] = useState(reduced ? share : 0)
  const shown = useCountUp(done)

  useEffect(() => {
    if (reduced) {
      setP(share)
      return
    }
    let inner = 0
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setP(share))
    })
    return () => {
      cancelAnimationFrame(outer)
      cancelAnimationFrame(inner)
    }
  }, [share, reduced])

  return (
    <span
      className={'kit-ring' + (total > 0 && done >= total ? ' kit-full' : '')}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      aria-valuetext={done + ' of ' + total}
      style={{ '--kit-p': p } as CSSProperties}
    >
      <b aria-hidden="true">
        {shown}/{total}
      </b>
    </span>
  )
}
